import { MatchNav } from '@src/components/common/MatchNav';
import { COMPACT_TOOLTIP_STYLE } from '@src/components/common/tooltipStyles';
import React from 'react';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import { PanelButton } from './panelButtonStyles';

interface HighlightTogglePillProps {
  /** Visible label, Title Case (e.g. "Quotes"). */
  label: string;
  ariaLabel: string;
  /** Whether the highlights are currently shown. */
  isOn: boolean;
  /** Total matches in the story. 0 → the pill is inert and shows no nav. */
  total: number;
  /** 1-based index of the active match, displayed as `current/total`. */
  current: number;
  onToggle: () => void;
  onPrev: () => void;
  onNext: () => void;
  prevLabel: string;
  nextLabel: string;
  /** Content tint pair, e.g. `var(--content-quotes-text)` / `-bg`. */
  textColor: string;
  bgColor: string;
  tooltipId: string;
  /** Tooltip copy per state: on, off, and no-matches. */
  tooltipOn: string;
  tooltipOff: string;
  tooltipEmpty: string;
  /**
   * A Show Only filter is hiding the non-matching paragraphs, so marking
   * would land on text that isn't rendered — the pill goes inert and says so.
   */
  blocked?: boolean;
  onFocus?: () => void;
  groupTestId: string;
  buttonTestId: string;
}

/**
 * A transcript-header highlight toggle fused with its `‹ n/N ›` match nav
 * into one fixed-footprint pill (PLAT-11039) — the Quotes and Clips
 * controls. Extracted from `Transcript.tsx` per CLAUDE.md → "Architecture
 * Guardrails" rule 5; the two hosts differ only in copy and content tint.
 *
 * The nav zone renders whenever matches exist and always shows the same
 * `n/N` text — dimmed while the toggle is off (an arrow click turns it on
 * via `onToggle`), content-tinted while on — so toggling recolors the pill
 * without ever resizing it, and neighboring controls never shift.
 *
 * The pill goes inert — muted, no hover feedback, no nav zone, `aria-disabled`
 * rather than native `disabled` so the explanatory tooltip still fires — when
 * the story has no matches, or when `blocked` says a Show Only filter is
 * hiding the paragraphs it would mark.
 *
 * Purely presentational — all state and navigation live with the host.
 */
export const HighlightTogglePill: React.FC<HighlightTogglePillProps> = ({
  label,
  ariaLabel,
  isOn,
  total,
  current,
  onToggle,
  onPrev,
  onNext,
  prevLabel,
  nextLabel,
  textColor,
  bgColor,
  tooltipId,
  tooltipOn,
  tooltipOff,
  tooltipEmpty,
  blocked,
  onFocus,
  groupTestId,
  buttonTestId,
}) => {
  // One resolved reason for being inert. An empty story wins over a Show Only
  // filter: with nothing to mark, pointing at the filter is a red herring.
  const disabledReason =
    total === 0
      ? tooltipEmpty
      : blocked
        ? `Turn off "Show Only" to mark ${label.toLowerCase()}`
        : null;
  const isDisabled = !!disabledReason;
  return (
    <>
      <ToggleNavGroup
        data-testid={groupTestId}
        $on={isOn && !isDisabled}
        $disabled={isDisabled}
      >
        <TogglePillButton
          tabIndex={-1}
          isPressed={isOn && !isDisabled}
          $disabled={isDisabled}
          $textColor={textColor}
          $bgColor={bgColor}
          aria-disabled={isDisabled}
          onFocus={onFocus}
          onClick={isDisabled ? undefined : onToggle}
          aria-label={ariaLabel}
          data-testid={buttonTestId}
          data-tooltip-id={tooltipId}
          data-tooltip-content={
            disabledReason ?? (isOn ? tooltipOn : tooltipOff)
          }
        >
          {label}
        </TogglePillButton>
        {!isDisabled && (
          <MatchNav
            attached
            inactive={!isOn}
            onActivate={onToggle}
            current={current}
            total={total}
            onPrev={onPrev}
            onNext={onNext}
            prevLabel={prevLabel}
            nextLabel={nextLabel}
            color={isOn ? textColor : 'var(--text-secondary)'}
            background={isOn ? bgColor : undefined}
          />
        )}
      </ToggleNavGroup>
      {/* Above the pill (PLAT-11182): below, these tooltips dropped straight
          onto the Highlights / Story Topics menus, which open downward from
          the same toolbar row and sit right beside them. */}
      <Tooltip
        id={tooltipId}
        style={COMPACT_TOOLTIP_STYLE}
        place="top"
        positionStrategy="fixed"
      />
    </>
  );
};

// Fuses the toggle with its nav: when the nav segment is mounted the toggle's
// right corners square off so the two read as one pill (the nav's `attached`
// variant squares its left corners and paints the same content tint). There is
// no divider between them (PLAT-11039) — the shared tint is the join, so the
// label's right padding drops to 3px and label + counter read as one group.
//
// Hover lives HERE, not on the label button: the resting pill highlights as a
// single control (label + counter together) whichever half the pointer is
// over. The arrows keep their own hover tint, which layers on top of this so
// they still read as separate hit targets. Pressed pills already carry the
// content tint across both halves, so they take no hover fill.
const ToggleNavGroup = styled.div<{ $on?: boolean; $disabled?: boolean }>`
  display: inline-flex;
  align-items: stretch;
  height: 100%;
  border-radius: var(--radius-button);
  transition: background-color 150ms ease-out;

  ${p =>
    !p.$on &&
    !p.$disabled &&
    `&:hover {
      background-color: var(--surface-elevated);
    }

    &:hover > * {
      color: var(--foreground);
    }`}

  > button:first-child:not(:only-child) {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    padding-right: 3px;
  }
`;

// Text-label pill (PLAT-10763): "Quotes" / "Clips" read unambiguously where
// the icons did not. Resting = neutral; active = the host's content tint.
// Title Case at 11px unifies the header row's label type with the Highlights
// and Story Topics pills, and stays compact — the row is space-constrained.
const TogglePillButton = styled(PanelButton)<{
  $disabled?: boolean;
  $textColor: string;
  $bgColor: string;
}>`
  padding: 3px 8px;
  /* Same size as the language trigger (var(--text-xs)) so all header labels
     read as one family — accepted trade: the row is tighter when several
     controls are active (PLAT-11039). */
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  background-color: ${props =>
    props.isPressed ? props.$bgColor : 'transparent'};
  color: ${props =>
    props.isPressed ? props.$textColor : 'var(--text-secondary)'};

  /* No own hover fill while resting — ToggleNavGroup paints the hover across
     the whole fused control, and a second fill here would double up over it. */
  &:hover {
    background-color: ${props =>
      props.isPressed ? props.$bgColor : 'transparent'};
    color: ${props =>
      props.isPressed ? props.$textColor : 'var(--foreground)'};
  }

  /* No matches in the story → inert: muted, no hover/active feedback; the
     tooltip explains why. */
  ${props =>
    props.$disabled &&
    `
    color: var(--text-muted);
    cursor: default;

    &:hover {
      background-color: transparent;
      color: var(--text-muted);
    }

    &:active {
      background-color: transparent;
      scale: 1;
    }
  `}
`;
